import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { adminLogin as apiLogin, adminLogout as apiLogout, getAdminToken, adminFetch } from "@/lib/api";
import { markAdminDevice } from "@/lib/admin-exclude";

interface AdminUser {
  id?: string | number;
  email: string;
}

interface AuthState {
  user: AdminUser | null;
  session: { token: string } | null;
  isAdmin: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthState | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null);
  const [token, setToken] = useState<string | null>(getAdminToken());
  const [loading, setLoading] = useState(true);

  // On mount, validate existing token (best-effort)
  useEffect(() => {
    const existing = getAdminToken();
    if (!existing) {
      setLoading(false);
      return;
    }
    adminFetch("/admin/me")
      .then((data: any) => {
        const u = data?.user || data;
        if (u?.email) { setUser({ id: u.id, email: u.email }); markAdminDevice(); }
        else apiLogout();
      })
      .catch(() => {
        // Token invalid or endpoint missing — clear it
        apiLogout();
        setToken(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      setToken(null);
      setUser(null);
    };
    window.addEventListener("pdn-admin-unauthorized", handleUnauthorized);
    return () => window.removeEventListener("pdn-admin-unauthorized", handleUnauthorized);
  }, []);

  const signIn = async (email: string, password: string) => {
    try {
      const data: any = await apiLogin(email, password);
      const t = data?.token;
      const u = data?.user || { email };
      if (!t) return { error: "No token returned" };
      setToken(t);
      setUser({ id: u.id, email: u.email });
      markAdminDevice();
      return { error: null };
    } catch (err: any) {
      return { error: err?.message ?? "Login failed" };
    }
  };

  const signOut = async () => {
    try {
      await adminFetch("/logout", { method: "POST" });
    } catch {
      // best-effort: still clear local state below
    }
    apiLogout();
    setToken(null);
    setUser(null);
  };

  const isAdmin = !!user && !!token;

  return (
    <AuthContext.Provider value={{ user, session: token ? { token } : null, isAdmin, loading, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
